import { useSearchParams } from 'react-router-dom';
import { useMemo, useCallback } from 'react';
import {
  DEFAULT_PAGINATION_PAGE,
  DEFAULT_PAGINATION_LIMIT,
} from '../const/default-paginated-table-data.const';
import { SelectedFilter } from '../types/filters.types';
import { filtersToQueryParams } from '../utils/filterToQueryParams.util';

interface UseFilters {
  page: number;
  limit: number;
  filters?: string; // Raw filter string from URL
  searchString?: string; // Search string
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
}

export const useFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get('page');
  const limit = searchParams.get('limit');
  const filtersParam = searchParams.get('filters');
  const searchString = searchParams.get('searchString');
  const sortField = searchParams.get('sortField');
  const sortOrder = searchParams.get('sortOrder') as 'asc' | 'desc' | null;

  const queryParams = useMemo<UseFilters>(
    () => ({
      page: page ? Number(page) : DEFAULT_PAGINATION_PAGE,
      limit: limit ? Number(limit) : DEFAULT_PAGINATION_LIMIT,
      filters: filtersParam || undefined,
      searchString: searchString || undefined,
      sortField: sortField || undefined,
      sortOrder: sortOrder || undefined,
    }),
    [page, limit, filtersParam, searchString, sortField, sortOrder]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      searchParams.set('page', newPage.toString());
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      searchParams.set('limit', pageSize.toString());
      searchParams.set('page', '1');
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleFilterChange = useCallback(
    (filters: SelectedFilter[]) => {
      // Convert filters to query string
      const filterQueryString = filtersToQueryParams(filters);

      if (filterQueryString) {
        searchParams.set('filters', filterQueryString);
      } else {
        searchParams.delete('filters');
      }

      // Reset to page 1 when filters change
      searchParams.set('page', '1');
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      if (value && value.trim() && value.trim().length >= 2) {
        searchParams.set('searchString', value.trim());
        // Reset to page 1 when search changes
        searchParams.set('page', '1');
        setSearchParams(searchParams);
      } else {
        searchParams.delete('searchString');
        // Reset to page 1 when search is cleared
        searchParams.set('page', '1');
        setSearchParams(searchParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleSortChange = useCallback(
    (field: string, order?: 'asc' | 'desc') => {
      if (field) {
        searchParams.set('sortField', field);
        if (order) {
          searchParams.set('sortOrder', order);
        } else {
          searchParams.delete('sortOrder');
        }
      } else {
        searchParams.delete('sortField');
        searchParams.delete('sortOrder');
      }

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  return {
    queryParams,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
  };
};
